import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Info, Building2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Company selector for Super Admins without a company_id
 * Shows a dropdown to select which company to configure integrations for
 */
const SuperAdminCompanySelector = ({ 
  selectedCompanyId, 
  onCompanyChange,
  integrationName = "this integration"
}) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Only show for super admins without a company_id
  const isSuperAdmin = user?.is_super_admin;
  const needsCompanySelector = isSuperAdmin && !user?.company_id;
  
  useEffect(() => {
    if (needsCompanySelector) {
      loadCompanies();
    } else {
      setLoading(false);
    }
  }, [needsCompanySelector]);
  
  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/super-admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  
  // Don't render if not needed
  if (!needsCompanySelector) {
    return null;
  }
  
  if (loading) {
    return (
      <div className="mb-6 p-4 bg-wt-surface border border-wt-border rounded-xl animate-pulse">
        <div className="h-6 bg-wt-border rounded w-48 mb-3"></div>
        <div className="h-10 bg-wt-border rounded w-80"></div>
      </div>
    );
  }
  
  return (
    <div className="mb-6 p-4 bg-wt-surface border border-wt-border rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <Info className="h-5 w-5 text-wt-gold" />
        <span className="text-wt-text-primary font-medium">Super Admin Mode</span>
      </div>
      <p className="text-sm text-wt-text-muted mb-3">
        Select a company to configure {integrationName} for:
      </p>
      <Select value={selectedCompanyId} onValueChange={onCompanyChange}>
        <SelectTrigger className="w-full md:w-80 bg-wt-paper border-wt-border text-wt-text-primary">
          <SelectValue placeholder="Select a company..." />
        </SelectTrigger>
        <SelectContent>
          {companies.map((company) => (
            <SelectItem key={company.id} value={company.id}>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {company.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default SuperAdminCompanySelector;

// Helper hook for integration pages
export const useSuperAdminCompany = () => {
  const { user } = useAuth();
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  
  const isSuperAdmin = user?.is_super_admin;
  const needsCompanySelector = isSuperAdmin && !user?.company_id;
  
  // Get effective company_id to use for API calls
  const getEffectiveCompanyId = () => {
    if (needsCompanySelector) {
      return selectedCompanyId || null;
    }
    return null; // Let backend use user's company_id
  };
  
  return {
    selectedCompanyId,
    setSelectedCompanyId,
    needsCompanySelector,
    getEffectiveCompanyId,
    isReady: !needsCompanySelector || !!selectedCompanyId
  };
};
